import { useContext, useEffect, useState } from 'react';

import {
	BookOutlined,
	DeleteOutlined,
	EditOutlined,
	ReloadOutlined,
	SearchOutlined,
	UnorderedListOutlined,
} from '@ant-design/icons';
import {
	Button,
	Checkbox,
	Flex,
	Input,
	Popconfirm,
	Space,
	Spin,
	Table,
	Tooltip,
	message,
} from 'antd';
import { useNavigate } from 'react-router-dom';

import { SubscriptionContext } from '@/App';
import { useDeleteQACollection } from '@/api/batch_process';
import { useGetQACollections } from '@/api/monitor_collections';
import AddNewQACollectionDrawer from '@/components/drawers/AddNewQACollectionDrawer';
import EditQACollectionDrawer from '@/components/drawers/EditQACollectionDrawer';
import { SUBSCRIPTIONS_TYPES_ENUM } from '@/const';
import { useDebounce } from '@/hooks';
import hasSubscriptionType from '@/lib/helpers/hasSubscriptionType';
import { PageContext } from '@/providers/PageContextProvider';
import { IPagination } from '@/ts';

import { columns } from './data';

export default function AllQA() {
	const navigate = useNavigate();
	const { subscriptions, subscriptionLoading } =
		useContext(SubscriptionContext);
	const { setPageTitle, openDrawer, setIsCopilotCreditsCounterVisible } =
		useContext(PageContext);
	const [pagination, setPagination] = useState<IPagination>({
		page: 1,
		pageCount: 1,
		total: 0,
		limit: 10,
	});
	const [filterUserCollections, setFilterUserCollections] = useState(
		localStorage?.getItem('sv_show_user_qa_collection') === '1' ? true : false,
	);
	const [filterFavouriteCollections, setFilterFavouriteCollections] = useState(
		localStorage?.getItem('sv_show_favourite_qa_collection') === '1'
			? true
			: false,
	);

	const [search, setSearch] = useState('');
	const [sortBy, setSortBy] = useState<'created_at'>('created_at');
	const [sortOrder, setSortOrder] = useState<'desc' | 'asc'>('desc');

	const debouncedSearch = useDebounce(search);

	const { data, isLoading, isFetching, refetch } = useGetQACollections({
		page: pagination.page,
		limit: pagination.limit,
		sortBy,
		sortOrder,
		search: debouncedSearch.length >= 3 ? debouncedSearch : '',
		isUserOnly: filterUserCollections,
		favourites: filterFavouriteCollections,
	});

	const { mutate: deleteQACollection, isLoading: isDeleteQACollectionLoading } =
		useDeleteQACollection();

	const total = data?.data?.data?.total ?? 1;
	const items = data?.data?.data?.items ?? [];

	useEffect(() => {
		setPageTitle('QA collections');
		setIsCopilotCreditsCounterVisible(true);

		return () => {
			setPageTitle('');
			setIsCopilotCreditsCounterVisible(false);
		};
	}, []);

	useEffect(() => {
		refetch();
	}, [filterUserCollections, sortOrder, pagination]);

	useEffect(() => {
		if (!subscriptionLoading && subscriptions && subscriptions?.length > 0) {
			if (
				!hasSubscriptionType(subscriptions, SUBSCRIPTIONS_TYPES_ENUM.copilot)
			) {
				navigate('/app/dashboard/product-main-menu');
			}
		} else if (
			!subscriptionLoading &&
			subscriptions &&
			subscriptions?.length === 0
		) {
			navigate('/app/dashboard/product-main-menu');
		}
	}, [subscriptions, subscriptionLoading]);

	const onTableChange = (__: any, _: any, sorter: any) => {
		const { field = 'created_at', order = 'ascend' } = sorter;

		if (field) {
			setSortBy(field);
			// setSearchParams((searchParams) => {
			// 	searchParams.set('sortBy', field);
			// 	return searchParams;
			// });
		}

		if (order) {
			setSortOrder(order === 'ascend' ? 'asc' : 'desc');
			// setSearchParams((searchParams) => {
			// 	searchParams.set('sortOrder', order === 'ascend' ? 'asc' : 'desc');
			// 	return searchParams;
			// });
		}
	};

	const onNewQACollectionClick = () => {
		// setDrawer
		openDrawer('add-new-qa-collection');
	};

	const onDeleteClick = (id: number) => {
		deleteQACollection(id.toString(), {
			onSuccess: () => {
				message.success('Removed');
				// setSearch("");
			},
			onError: (error) => {
				const err = error as any;
				console.error('Error deleting collection:', err.response.data.message);
				message.error(err.response.data.message);
			},
		});
	};

	const onEditClick = (record: any) => {
		openDrawer('edit-qa-collection', record);
	};

	return (
		<Spin spinning={isLoading || isFetching}>
			<div>
				<Flex
					align="center"
					justify="flex-end"
					style={{ marginBottom: '1rem' }}
				>
					<Space>
						<Checkbox
							checked={filterUserCollections}
							onChange={(e) => {
								const checked = e.target.checked;
								setFilterUserCollections(checked);
								localStorage.setItem(
									'sv_show_user_qa_collection',
									checked ? '1' : '0',
								);
							}}
						>
							my collections
						</Checkbox>
						<Checkbox
							checked={filterFavouriteCollections}
							onChange={(e) => {
								const checked = e.target.checked;
								setFilterFavouriteCollections(checked);
								localStorage.setItem(
									'sv_show_favourite_qa_collection',
									checked ? '1' : '0',
								);
							}}
						>
							favourites
						</Checkbox>
						<Tooltip title="Refresh" placement="left">
							<Button onClick={() => refetch()} icon={<ReloadOutlined />} />
						</Tooltip>
					</Space>
				</Flex>

				<Flex vertical gap={16} style={{ width: '100%' }}>
					<Flex
						gap={8}
						justify="space-between"
						align="center"
						style={{ width: '100%' }}
					>
						<Input
							placeholder="Search..."
							suffix={<SearchOutlined />}
							style={{ width: '300px', maxWidth: '300px' }}
							value={search}
							onChange={(e) => setSearch(e.target.value)}
						/>
						<Button type="primary" onClick={onNewQACollectionClick}>
							New QA collection
						</Button>
					</Flex>

					<Table
						loading={isLoading || isFetching}
						// style={{ width: '100%' }}
						onChange={onTableChange}
						dataSource={items}
						columns={[
							...columns,
							{
								title: 'Actions',
								dataIndex: 'actions',
								key: 'actions',
								width: 80,
								ellipsis: false,
								fixed: 'right',
								// flex: true,
								render: (_: any, record: any) => {
									return (
										<Flex justify="center" align="center" gap={8}>
											{/* <Tooltip title="View">
												<Button
													size="small"
													onClick={() =>
														navigate(
															`/app/dashboard/content-monitor/${record?.id}`,
														)
													}
												>
													<UnorderedListOutlined />
												</Button>
											</Tooltip> */}
											<Tooltip title="Edit">
												<Button
													size="small"
													onClick={() => onEditClick(record)}
												>
													<EditOutlined />
												</Button>
											</Tooltip>
											<Tooltip title="Remove">
												<Popconfirm
													title={`Sure to remove collection?`}
													onConfirm={() => onDeleteClick(Number(record?.id))}
													placement="topLeft"
												>
													<Button danger size="small">
														<DeleteOutlined />
													</Button>
												</Popconfirm>
											</Tooltip>
										</Flex>
									);
								},
							},
						]}
						rowKey={'id'}
						bordered
						scroll={{ x: 1100 }}
						pagination={{
							total,
							showSizeChanger: true,
							current: pagination.page,
							pageSizeOptions: [10, 25, 50],
							onChange: (page: number, pageSize: number) => {
								setPagination((cur) => ({ ...cur, page, limit: pageSize }));
							},
						}}
					/>
				</Flex>
			</div>
			<AddNewQACollectionDrawer />
			<EditQACollectionDrawer />
		</Spin>
	);
}
